import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import FriendCard from '../friends/FriendCard';
import githubPng from '../../styles/images/github.png';
import figmaPng from '../../styles/images/figma.png';
import zhihuPng from '../../styles/images/zhihu.png';
import rednotePng from '../../styles/images/rednote.png';
import tiktokPng from '../../styles/images/tiktok.png';
import officalAccountPng from '../../styles/images/offical_account.png';
import bilibiliPng from '../../styles/images/bilibili.png';
import jikePng from '../../styles/images/jike.png';
import customPng from '../../styles/images/custom.png';
import wechatPng from '../../styles/images/wechat.png';
import weiboPng from '../../styles/images/weibo.png';
import SocialCard from '../social/SocialCard';
import TeamCard from '../team/TeamCard';
import socialLinkService from '../../services/socialLinkService';

import WaterfallLayout from '../common/WaterfallLayout';
import { DeleteOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import useAppMessage from '../../hooks/useAppMessage';
import friendService from '../../services/friendService';
import teamService from '../../services/teamService';

export default function PersonalTab({
  posts = [],
  postsLoading = false,
  onShowCreateModal,
  onShowSocialDrawer,
  userId, // 当前查看用户id
  socialLinks = [],
  isSelf = false, // 是否本人主页
  onRefreshSocialLinks, // 新增：刷新社交链接的回调函数
}) {
  const { message, contextHolder } = useAppMessage();

  // 使用 stable ref 避免 message 依赖循环
  const stableMessageRef = useRef(message);
  stableMessageRef.current = message;
  const searchTimeout = useRef(null);
  const [activeTab, setActiveTab] = useState('main');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState({});
  const [copyStatus, setCopyStatus] = useState({ copied: false, message: '' });
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deletingSocialLink, setDeletingSocialLink] = useState(null);
  

  // 根据传入的posts生成渲染数据
  const displayPosts = useMemo(() => posts || [], [posts]);

  // 新增：社交媒体数据
  const [socialLoading, setSocialLoading] = useState(false);

  // 使用 useMemo 来处理 socialLinks，避免不必要的重新渲染
  const localSocialLinks = useMemo(() => {
    return socialLinks && Array.isArray(socialLinks) ? socialLinks : [];
  }, [socialLinks]);

  // 新增：动态帖子数据
  const [activityPosts, setActivityPosts] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // 新增：团队数据
  const [teamData, setTeamData] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);



  // 监听tab切换为activity时自动请求动态
  useEffect(() => {
    if (activeTab === 'activity' && userId) {
      setActivityLoading(true);
      friendService
        .searchContents({
          type: 'article', // 动态tab使用article类型
          filter: {
            exprs: [
              {
                field: 'author_id',
                op: 'EQ',
                values: [userId]
              }
            ]
          },
          sort: { field: 'inserted_at', desc: true },
          pagination: {
            page: 1,
            per_page: 20,
          },
        })
        .then((res) => {
          // 兼容API返回结构
          setActivityPosts(res.contents || []);
        })
        .catch(() => setActivityPosts([]))
        .finally(() => setActivityLoading(false));
    } else if (activeTab !== 'activity') {
      // 当切换到其他tab时，清空动态数据
      setActivityPosts([]);
    }
  }, [activeTab, userId]);

  // 监听tab切换为teams时自动请求团队数据
  useEffect(() => {
    if (activeTab === 'teams' && userId) {
      setTeamLoading(true);
      teamService
        .getUserTeams(userId)
        .then((res) => {
          console.log('获取用户团队数据:', res);
          // 兼容API返回结构，支持teams和groups字段
          setTeamData(res.teams || res.groups || []);
        })
        .catch((error) => {
          console.error('获取用户团队数据失败:', error);
          setTeamData([]);
        })
        .finally(() => setTeamLoading(false));
    } else if (activeTab !== 'teams') {
      // 当切换到其他tab时，清空团队数据
      setTeamData([]);
    }
  }, [activeTab, userId]);



  // tabs 配置
  const tabs = [
    { key: 'main', label: '主页' },
    { key: 'activity', label: '动态' },
    { key: 'teams', label: '团队' },
    // { key: 'like', label: '点赞' },
    // { key: 'fav', label: '收藏' },
  ];

  const handleSocialCardClick = useCallback((iconKey, link, label) => {
    let type = '';

    // 微信、微信公众号
    if (iconKey === 'wechat' || iconKey === 'offical_account') {
      type = 'qrcode';
    }
    // github、figma、知乎
    else if (
      iconKey === 'github' ||
      iconKey === 'figma' ||
      iconKey === 'zhihu'
    ) {
      type = 'profile';
    }
    // 小红书、抖音、即刻、微博、bilibili
    else if (
      ['rednote', 'tiktok', 'jike', 'weibo', 'bilibili'].includes(iconKey)
    ) {
      type = 'link';
    }
    // 自定义
    else if (iconKey === 'custom') {
      type = 'custom';
    }

    // 找到对应的社交链接以获取image_url
    const socialLink = localSocialLinks.find(
      (socialLink) => socialLink.type === iconKey
    );
    const imageUrl = socialLink?.image_url;

    setModalType(type);
    setModalData({ iconKey, link, label, imageUrl });
    setModalVisible(true);
  }, [localSocialLinks]);

  const getModalIcon = useCallback((iconKey) => {
    const iconMap = {
      wechat: wechatPng,
      weibo: weiboPng,
      figma: figmaPng,
      zhihu: zhihuPng,
      rednote: rednotePng,
      github: githubPng,
      tiktok: tiktokPng,
      offical_account: officalAccountPng,
      bilibili: bilibiliPng,
      jike: jikePng,
      custom: customPng,
    };

    return iconMap[iconKey] || customPng;
  }, []);

  const getModalTitle = useCallback((iconKey) => {
    const titleMap = {
      wechat: '扫描二维码找到我',
      offical_account: '扫描二维码找到我',
      github: '使用Github搜索，找到我',
      figma: '使用Figma搜索，找到我',
      zhihu: '使用知乎搜索，找到我',
      rednote: '复制到浏览器，打开小红书',
      tiktok: '复制到浏览器，打开抖音',
      jike: '复制到浏览器，打开即刻',
      weibo: '复制到浏览器，打开微博',
      bilibili: '复制到浏览器，打开Bilibili',
      custom: '复制到浏览器打开',
    };

    return titleMap[iconKey] || '复制到浏览器打开';
  }, []);

  // 复制功能
  const copyToClipboard = useCallback(async (text, type = 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      const message = type === 'username' ? '用户名已复制' : '链接已复制';
      setCopyStatus({ copied: true, message });

      // 3秒后重置状态
      setTimeout(() => {
        setCopyStatus({ copied: false, message: '' });
      }, 3000);
    } catch (err) {
      console.error('复制失败:', err);
      setCopyStatus({ copied: false, message: '复制失败' });
    }
  }, []);

  // 跳转链接功能
  const openLink = useCallback((link) => {
    if (link && link !== '#') {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // 显示删除确认模态框
  const handleDeleteCard = useCallback((iconKey, link) => {
    // 找到对应的社交链接 - 使用 type 和 url 来精确定位
    const socialLink = localSocialLinks.find(
      (socialLink) => socialLink.type === iconKey && socialLink.url === link
    );
    if (!socialLink) {
      stableMessageRef.current.error('未找到要删除的社交链接');
      return;
    }

    setDeletingSocialLink(socialLink);
    setDeleteConfirmVisible(true);
  }, [localSocialLinks]); // 移除 message 依赖

  // 确认删除社交链接
  const confirmDeleteSocialLink = useCallback(async () => {
    if (!deletingSocialLink) return;

    try {
      // 调用删除API
      await socialLinkService.deleteSocialLink(
        deletingSocialLink.type,
        deletingSocialLink.display_name
      );

      console.log('删除社交链接:', deletingSocialLink);

      // 关闭所有模态框
      setModalVisible(false);
      setDeleteConfirmVisible(false);
      setCopyStatus({ copied: false, message: '' });
      setDeletingSocialLink(null);

      stableMessageRef.current.success('社交链接删除成功');
      
      // 通知父组件刷新社交链接数据
      if (onRefreshSocialLinks) {
        onRefreshSocialLinks();
      }
    } catch (error) {
      console.error('删除社交链接失败:', error);
      stableMessageRef.current.error(error.message || '删除失败，请重试');
    }
  }, [deletingSocialLink, onRefreshSocialLinks]); // 添加 onRefreshSocialLinks 依赖

  // 获取用户名（从链接中提取）
  const getUsernameFromLink = useCallback((link, iconKey) => {
    if (!link || link === '#') return 'username';

    try {
      const url = new URL(link);
      // 对 pathname 进行解码，防止中文被编码
      const pathname = decodeURIComponent(url.pathname);

      // 根据不同平台提取用户名
      switch (iconKey) {
        case 'github':
          return pathname.split('/')[1] || 'username';
        case 'zhihu':
          // 知乎格式: https://www.zhihu.com/people/username
          return pathname.split('/')[2] || 'username';
        case 'figma':
          // Figma格式: https://www.figma.com/@username
          const figmaUsername = pathname.split('/')[1] || 'username';
          // 移除开头的@符号
          return figmaUsername.startsWith('@')
            ? figmaUsername.slice(1)
            : figmaUsername;
        default:
          return 'username';
      }
    } catch {
      return 'username';
    }
  }, []);

  const renderModal = useCallback(() => {
    if (!modalVisible) return null;

    const { iconKey, link, label, imageUrl } = modalData;
    const modalIcon = getModalIcon(iconKey);
    const modalTitle = getModalTitle(iconKey);
    const username = getUsernameFromLink(link, iconKey);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 relative">
          {/* Close button */}
          <button
            onClick={() => {
              setModalVisible(false);
              setCopyStatus({ copied: false, message: '' });
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>

          {/* Header */}
          <div className="flex items-center mb-6">
            <img src={modalIcon} alt={label} className="w-12 h-12 mr-3" />
            <h2 className="text-lg font-medium text-gray-900">{modalTitle}</h2>
          </div>

          {/* Content based on modal type */}
          {modalType === 'qrcode' && (
            <div className="text-center">
              <div className="w-64 h-64 bg-white mx-auto mb-4 rounded-lg flex items-center justify-center border border-gray-200">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="QR Code"
                    className="w-60 h-60 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {!imageUrl && (
                  <div className="w-60 h-60 flex items-center justify-center text-gray-400 text-sm">
                    暂无二维码图片
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-4">扫描二维码添加好友</p>
              <div className="flex justify-center gap-3">
                {isSelf && (
                  <DeleteOutlined
                    onClick={() => handleDeleteCard(iconKey, link)}
                    className="text-gray-500 hover:text-gray-600 cursor-pointer text-2xl"
                  />
                )}
                {/* <button
                  onClick={() =>
                    copyToClipboard(link || 'wechat://addfriend', 'link')
                  }
                  className="bg-purple-600 text-white px-6 py-2 rounded-full font-medium hover:bg-purple-700 transition-colors text-sm"
                >
                  复制微信号
                </button> */}
              </div>
            </div>
          )}

          {modalType === 'profile' && (
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-900 mb-4 truncate">
                @{username}
              </div>
              <div
                className={`mb-6 ${
                  copyStatus.copied ? 'text-green-500' : 'text-purple-500'
                }`}
              >
                {copyStatus.message || '点击下方按钮复制用户名'}
              </div>
              <div className="flex justify-center gap-3">
                {/* delete card button */}
                {isSelf && (
                  <DeleteOutlined
                    onClick={() => handleDeleteCard(iconKey, link)}
                    className="text-gray-500 hover:text-gray-600 cursor-pointer text-2xl"
                  />
                )}
                <button
                  onClick={() => copyToClipboard(`@${username}`, 'username')}
                  className="bg-[var(--color-primary)] text-white px-8 py-3 rounded-full font-medium hover:bg-[var(--color-primary-hover)] transition-colors"
                >
                  {copyStatus.copied ? '已复制' : '复制用户名'}
                </button>
              </div>
            </div>
          )}

          {modalType === 'link' && (
            <div className="text-center">
              <div className="text-gray-600 mb-4 break-all text-sm">
                {link || 'https://www.xiaohongshu.com/user/......'}
              </div>

              <div
                className={`mb-6 ${
                  copyStatus.copied ? 'text-green-500' : 'text-purple-500'
                }`}
              >
                {copyStatus.message || '点击下方按钮复制链接'}
              </div>

              <div className="flex gap-3">
                {/* delete card button */}
                {isSelf && (
                  <DeleteOutlined
                    onClick={() => handleDeleteCard(iconKey, link)}
                    className="text-gray-500 hover:text-gray-600 cursor-pointer text-2xl"
                  />
                )}
                <button
                  onClick={() => copyToClipboard(link, 'link')}
                  className="flex-1 border border-[var(--color-primary)] text-[var(--color-primary)] px-6 py-3 rounded-full font-medium hover:bg-[var(--color-primary-bg)] transition-colors"
                >
                  {copyStatus.copied ? '已复制' : '复制链接'}
                </button>
              </div>
            </div>
          )}

          {modalType === 'custom' && (
            <div className="text-center">
              {/* 自定义类型显示图片预览 */}
              <div className="w-64 h-64 bg-white mx-auto mb-4 rounded-lg flex items-center justify-center border border-gray-200">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Custom Preview"
                    className="w-60 h-60 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {!imageUrl && (
                  <div className="w-60 h-60 flex items-center justify-center text-gray-400 text-sm">
                    暂无预览图片
                  </div>
                )}
              </div>

              <div className="text-gray-600 mb-4 break-all text-sm">
                {link || 'https://example.com/......'}
              </div>

              <div
                className={`mb-6 ${
                  copyStatus.copied ? 'text-green-500' : 'text-purple-500'
                }`}
              >
                {copyStatus.message || '点击下方按钮复制链接'}
              </div>

              <div className="flex gap-3">
                {/* delete card button */}
                {isSelf && (
                  <DeleteOutlined
                    onClick={() => handleDeleteCard(iconKey, link)}
                    className="text-gray-500 hover:text-gray-600 cursor-pointer text-2xl"
                  />
                )}
                <button
                  onClick={() => copyToClipboard(link, 'link')}
                  className="flex-1 border border-[var(--color-primary)] text-[var(--color-primary)] px-6 py-3 rounded-full font-medium hover:bg-[var(--color-primary-bg)] transition-colors"
                >
                  {copyStatus.copied ? '已复制' : '复制链接'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [modalVisible, modalData, modalType, copyStatus, isSelf, getModalIcon, getModalTitle, getUsernameFromLink, handleDeleteCard, copyToClipboard]);

  // 合并社交媒体和个人帖子数据
  const mainTabItems = useMemo(() => [
    ...(localSocialLinks || []).map((link) => ({
      type: 'social',
      key: link.type + (link.url || ''),
      component: (
        <SocialCard
          key={link.type + (link.url || '')}
          media={link.type}
          label={link.display_name || link.type}
          link={link.url}
          onCardClick={handleSocialCardClick}
        />
      ),
    })),
    ...(displayPosts || []).map((item) => ({
      type: item.thumb ? 'friend-with-image' : 'friend-without-image',
      key: item.id,
      component: <FriendCard key={item.id} {...item} />,
    })),
  ], [localSocialLinks, displayPosts]); // 移除 handleSocialCardClick 依赖，避免循环

  // 动态内容数据
  const activityItems = useMemo(() => 
    activityPosts.map((item) => ({
      type: item.thumb ? 'friend-with-image' : 'friend-without-image',
      key: item.id,
      component: <FriendCard key={item.id} {...item} />,
    })), [activityPosts]
  );

  // 团队数据
  const teamItems = useMemo(() => {
    return teamData.map((team) => {
      return {
        type: 'team',
        key: team.id,
        component: (
          <TeamCard
            key={team.id}
            team={team}
            userRole="member" // 默认角色，实际应该从API返回
            isCaptain={false} // 默认不是队长，实际应该从API返回
          />
        ),
      };
    });
  }, [teamData]);

  // 其他tab的数据
  const otherTabItems = useMemo(() => displayPosts.map((item) => ({
    type:
      item.type === 'link'
        ? 'social'
        : item.thumb
        ? 'friend-with-image'
        : 'friend-without-image',
    key: item.id || item.media || item.prompt,
    component:
      item.type === 'link' ? (
        <SocialCard
          media={item.media || item.prompt || 'custom'}
          label={item.label || item.media || item.prompt || '社交'}
          link={item.link || item.response || '#'}
          onCardClick={handleSocialCardClick}
        />
      ) : (
        <FriendCard {...item} />
      ),
  })), [displayPosts]); // 移除 handleSocialCardClick 依赖，避免循环

  // tab内容渲染
  const getTabContent = useCallback((tabKey) => {
    if (tabKey === 'main') {
      if (socialLoading || postsLoading) {
        return <div className="py-12 text-center text-gray-400">加载中...</div>;
      }

      return (
        <div className="pt-6">
          <WaterfallLayout
            items={mainTabItems}
            columns={4}
            gap="gap-4"
            heightMap={{
              social: '10rem',
              'friend-with-image': '20rem',
              'friend-without-image': '10rem',
            }}
          />
          {/* 底部按钮 - 只在 main tab 显示 */}
          <div className="flex justify-center items-center gap-4 mt-6 sticky bottom-4 left-0 right-0">
            {isSelf && (
              <>
                <button
                  className="bg-[var(--color-assist-3)] text-black rounded-full px-20 py-2 text-sm font-medium border-none"
                  onClick={onShowCreateModal}
                >
                  个人经历
                </button>

                <button
                  className="bg-[var(--color-assist-3)] text-black rounded-full px-20 py-2 text-sm font-medium border-none"
                  onClick={onShowSocialDrawer}
                >
                  社交媒体
                </button>
              </>
            )}
          </div>
        </div>
      );
    }
    if (tabKey === 'activity') {
      if (activityLoading) {
        return <div className="py-12 text-center text-gray-400">加载中...</div>;
      }
      if (!activityPosts || activityPosts.length === 0) {
        return <div className="py-12 text-center text-gray-400">暂无动态</div>;
      }
      
      return (
        <div className="pt-6">
          <WaterfallLayout
            items={activityItems}
            columns={4}
            gap="gap-4"
            heightMap={{
              'friend-with-image': '20rem',
              'friend-without-image': '10rem',
            }}
          />
        </div>
      );
    }

    if (tabKey === 'teams') {
      if (teamLoading) {
        return <div className="py-12 text-center text-gray-400">加载中...</div>;
      }
      if (!teamItems || teamItems.length === 0) {
        return <div className="py-12 text-center text-gray-400">暂无团队</div>;
      }
      
      return (
        <div className="pt-6">
          <WaterfallLayout
            items={teamItems}
            columns={3}
            gap="gap-6"
            heightMap={{
              'team': '16rem',
            }}
          />
        </div>
      );
    }

    // 其他tab逻辑保持不变
    if (postsLoading) {
      return <div className="py-12 text-center text-gray-400">加载中...</div>;
    }
    if (!displayPosts || displayPosts.length === 0) {
      return <div className="py-12 text-center text-gray-400">暂无内容</div>;
    }

    return (
      <div className="pt-6">
        <WaterfallLayout
          items={otherTabItems}
          columns={4}
          gap="gap-4"
          heightMap={{
            social: '10rem',
            'friend-with-image': '20rem',
            'friend-without-image': '10rem',
          }}
        />
      </div>
    );
  }, [
    socialLoading, 
    postsLoading, 
    mainTabItems, 
    activityLoading, 
    activityPosts, 
    teamLoading,
    teamItems,
    isSelf,
    otherTabItems,
    displayPosts,
    onShowCreateModal,
    onShowSocialDrawer
  ]);

  return (
    <div className="">
      <div className="w-full">
        {/* Tab Navigation */}
        <div className="flex rounded-t-2xl pt-4">
          {tabs.map((tab, index) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={
                `text-sm font-medium px-24 py-3 rounded-t-2xl relative ` +
                (activeTab === tab.key
                  ? 'text-gray-900 bg-[var(--color-chat-bg)]'
                  : 'text-gray-500')
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Tab Content */}
        <div
          className={`bg-[var(--color-chat-bg)] px-6 pb-6 rounded-b-2xl ${
            activeTab === 'main'
              ? ''
              : 'rounded-tl-2xl'
          }`}
        >
          {getTabContent(activeTab)}
        </div>
      </div>
      {/* Modal */}
      {renderModal()}

      {/* 删除社交链接确认模态框 */}
      <Modal
        open={deleteConfirmVisible}
        title="确认删除社交链接"
        onCancel={() => {
          setDeleteConfirmVisible(false);
          setDeletingSocialLink(null);
        }}
        onOk={confirmDeleteSocialLink}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <div>
          确定要删除这个
          <span className="font-medium text-gray-900">
            {deletingSocialLink?.display_name || deletingSocialLink?.type}
          </span>
          链接吗？此操作不可撤销。
        </div>
      </Modal>
      
      {/* 消息提示容器 */}
      {contextHolder}
    </div>
  );
}
